import {
  IPeerReviewWFState,
  ISubmissionWFState
} from '../types/workflow-phases';

const stateNames = {
  submission: {
    phaseNames: {
      editorial: [
        'assignment',
        'initialAssessment',
        'peerReview',
        'peerReviewInvitation',
        'peerReviewPending',
        'peerReviewFinished',
        'preProduction',
        'revisionAssessment',
        'revisionPeerReviewInvitation',
        'revisionPeerReviewPending',
        'revisionPeerReviewFinished',
        'formalAcceptance',
        'rejected',
        'withdrawn'
      ],
      author: [
        'draft',
        'immediateRevision',
        'revision',
        'transferOffered',
        'transferPending',
        'unsubmitted'
      ],
      expert: [
        'invited',
        'assessment',
        'draft',
        'reviewed',
        'expired',
        'declined',
        'interrupted'
      ],
      expertRecommendation: [
        'peerReview',
        'reject',
        'immediateRevision',
        'unsubmit'
      ]
    },
    endStateNames: ['accepted', 'rejected', 'withdrawn', 'formalAcceptance'],
    editorialSubmissionFilters: [
      'assignment',
      'initialAssessment',
      'peerReviewInvitation',
      'peerReviewPending',
      'peerReviewFinished',
      'preProduction',
      'revisionAssessment',
      'revisionPeerReviewInvitation',
      'revisionPeerReviewPending',
      'revisionPeerReviewFinished',
      'immediateRevision',
      'revision',
      'transferOffered',
      'transferPending',
      'unsubmitted'
    ]
  },
  review: {
    startStates: ['initial', 'invited', 'revisionInvited'],
    endStates: ['declined', 'closed']
  }
} as const;

// review states
export const reviewStartStates: readonly IPeerReviewWFState[] =
  stateNames.review.startStates;
export const reviewEndStates: readonly IPeerReviewWFState[] =
  stateNames.review.endStates;

// submission states and phases
export const editorialPhases = stateNames.submission.phaseNames.editorial;
export const authorPhases = stateNames.submission.phaseNames.author;
export const workPhases = [...editorialPhases, ...authorPhases] as const;
export const submissionWorkPhases =
  stateNames.submission.editorialSubmissionFilters;
export const endStates = stateNames.submission.endStateNames;
export const allPhases = [...workPhases, ...endStates];
export const expertPhases = stateNames.submission.phaseNames.expert;
export const expertActivePhases = workPhases.filter(
  phase => !(endStates as readonly string[]).includes(phase)
);
export const expertRecommendationPhases =
  stateNames.submission.phaseNames.expertRecommendation;

/**
 * Submission states from pre-production onwards. Peer review is over here: a
 * submission reaches pre-production once the editors have taken a decision on
 * the latest round, and `formalAcceptance` is only reachable from there, so
 * reviewers can no longer be invited, re-invited or reassigned.
 */
export const postPeerReviewStates: readonly ISubmissionWFState[] = [
  'preProduction',
  'formalAcceptance',
  'accepted'
];

export const editorialPhaseMap: Record<IEditorialPhase, string> = {
  assignment: 'Assignment',
  initialAssessment: 'Initial Assessment',
  peerReview: 'Peer Review',
  peerReviewInvitation: 'Peer Review Invitation',
  peerReviewPending: 'Peer Review Pending',
  peerReviewFinished: 'Peer Review Finished',
  preProduction: 'Pre Production',
  revisionAssessment: 'Revision Assessment',
  revisionPeerReviewInvitation: 'Revision Peer Review Invitation',
  revisionPeerReviewPending: 'Revision Peer Review Pending',
  revisionPeerReviewFinished: 'Revision Peer Review Decision',
  formalAcceptance: 'Formal Acceptance',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn'
};

export const workPhaseMap: Record<IWorkPhase, string> = {
  ...editorialPhaseMap,
  draft: 'Draft',
  revision: 'Revision',
  immediateRevision: 'Immediate Revision',
  transferOffered: 'Transfer Offer',
  transferPending: 'Incomplete Transfer',
  unsubmitted: 'Unsubmitted'
};

/**
 * Editorial phases that carry a task checklist in the submission sidebar.
 *
 * Ordered newest first: the Task Checklist Log renders completed phases in
 * this order, with the phase that can still be edited at the top.
 */
export const editorialChecklistPhases = [
  'preProduction',
  'revisionAssessment',
  'initialAssessment'
] as const;
export type IEditorialChecklistPhase =
  (typeof editorialChecklistPhases)[number];

/**
 * Workflow states in which each checklist phase can still be edited.
 */
export const editorialChecklistActiveStates: Record<
  IEditorialChecklistPhase,
  readonly ISubmissionWorkflowState[]
> = {
  initialAssessment: ['initialAssessment'],
  revisionAssessment: ['revisionAssessment'],
  preProduction: ['preProduction']
};

/**
 * States that count as a completed exit from a checklist phase.
 *
 * By default a phase is kept in the log as soon as it is visited: a revision
 * request is the normal way out of initial/revision assessment, and the
 * checklist is still part of the submission's history. Pre-production is the
 * exception. Its decision can be reverted back to revision assessment (and the
 * submission then sent to revision), so having been in `preProduction` is not
 * proof that the phase was completed. It only counts once the submission left
 * it towards one of these states — including `rejected`/`withdrawn`, where the
 * checklist was already in use and should stay in the record.
 */
export const editorialChecklistCompletionStates: Partial<
  Record<IEditorialChecklistPhase, readonly ISubmissionWorkflowState[]>
> = {
  preProduction: ['formalAcceptance', 'accepted', 'rejected', 'withdrawn']
};

export const allPhaseMap: Record<ISubmissionWorkflowState, string> = {
  ...workPhaseMap,
  rejected: 'Rejected',
  accepted: 'Accepted',
  withdrawn: 'Withdrawn'
};

export const roleTypes = ['author', 'reviewer', 'editorial', 'expert'] as const;
export type IRoleType = (typeof roleTypes)[number];
export type IPhaseOwnerType = IRoleType;

export type IEditorialPhase =
  (typeof stateNames)['submission']['phaseNames']['editorial'][number];
export type IAuthorPhase =
  (typeof stateNames)['submission']['phaseNames']['author'][number];
export type IExpertPhase =
  (typeof stateNames)['submission']['phaseNames']['expert'][number];

export type IWorkPhase = IEditorialPhase | IAuthorPhase;
export type IEndState =
  (typeof stateNames)['submission']['endStateNames'][number];

export type ISubmissionWorkflowState = IWorkPhase | IEndState;

export function isEditorialPhase(key: string): key is IEditorialPhase {
  return stateNames.submission.phaseNames.editorial.includes(
    key as IEditorialPhase
  );
}

export function isAuthorPhase(key: string): key is IAuthorPhase {
  return stateNames.submission.phaseNames.author.includes(key as IAuthorPhase);
}

export type IGroupedWorkPhases = Partial<Record<IWorkPhase, IWorkPhase[]>>;

/**
 * A map of work phases to editorial phases
 * This allows us to group work phases together for editorial role creation UI
 *
 * See getGroupedWorkPhases() in /submission-service/src/usecases/workflows.ts
 */
export const WORK_TO_EDITORIAL_PHASE_ASSOCIATION_MAP: Record<
  IWorkPhase,
  IEditorialPhase | null
> = {
  immediateRevision: 'initialAssessment',
  revision: 'revisionAssessment',
  revisionPeerReviewInvitation: 'revisionAssessment',
  revisionPeerReviewPending: 'revisionAssessment',
  revisionPeerReviewFinished: 'revisionAssessment',
  peerReviewInvitation: 'peerReview',
  peerReviewPending: 'peerReview',
  peerReviewFinished: 'peerReview',

  assignment: 'assignment',
  initialAssessment: 'initialAssessment',
  peerReview: 'peerReview',
  preProduction: 'preProduction',
  revisionAssessment: 'revisionAssessment',
  transferOffered: 'peerReview', // TODO not sure if this is correct. Which phase group does pendingTransfer belong?
  transferPending: 'assignment',
  formalAcceptance: 'formalAcceptance',
  rejected: 'rejected',
  withdrawn: 'withdrawn',

  draft: null,
  unsubmitted: null
};
